import React, { useState, useMemo, useEffect } from 'react';
import { gpuFpsTable } from '../data/mock'; 
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gamepad2, Monitor, Briefcase, Server,
  ShieldAlert, CheckCircle2, ChevronRight, Check,
  AlertTriangle, Star, TrendingUp, SkipForward,
  Zap, Package, ShoppingCart, ArrowRight, Loader2, Info
} from 'lucide-react';
import intel_logo from '../data/intel.png';
import amd_logo from '../data/amd.png';
import nvidia_logo from '../data/nvidia.png';
import radeon_logo from '../data/radeon.png';

// === Austrian Price Formatter Helper ===
const formatPrice = (num) => {
  if (isNaN(num)) return "0,00";
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// === Responsive Image Width based on Category ===
const getResponsiveImageWidth = (category, isWater = false) => {
  if (category === 'gpu') {
    return 'w-20 sm:w-24 lg:w-24 2xl:w-32';
  }
  if (category === 'ram') {
    return 'w-10 sm:w-12 lg:w-14 2xl:w-20';
  }
  if (category === 'storage' || category === 'storage2') {
    return 'w-10 sm:w-12 lg:w-14 2xl:w-[4.5rem]'; 
  }
  if (category === 'cooling' && isWater) {
    return 'w-16 sm:w-20 lg:w-24 2xl:w-[7.5rem]';
  }
  return 'w-10 lg:w-12 2xl:w-16';
};

const CATEGORY_ORDER = ['case', 'cpu', 'gpu', 'motherboard', 'ram', 'storage', 'storage2', 'psu', 'cooling', 'fans', 'os'];
const REQUIRED = ['cpu', 'gpu', 'motherboard', 'ram', 'storage', 'psu', 'case', 'cooling', 'os'];

// ⚠️ UPDATE THESE IDS: Replace 'case-3', 'cpu-4', etc. with your real WooCommerce Product IDs!
const DEFAULT_NO_BUDGET_REC = {
  case: 'case-3', cpu: 'cpu-4', gpu: 'gpu-3', motherboard: 'mb-2', ram: 'ram-2', storage: 'ssd-1', storage2: '', psu: 'psu-2', cooling: 'aio-3', fans: '', os: 'os-1'
};

const categoryMeta = {
  case: { label: 'Gehäuse', short: 'Gehäuse', hint: 'Form, Design und Airflow. Dein Gehäuse bestimmt nicht nur die Optik, sondern auch Kühlleistung, Erweiterbarkeit und Lautstärke.' },
  cpu: { label: 'Prozessor (CPU)', short: 'CPU', hint: 'Das Herzstück deines PCs. Verantwortlich für Systemgeschwindigkeit, Ladezeiten und die Rechenleistung in Spielen und Anwendungen.' },
  gpu: { label: 'Grafikkarte (GPU)', short: 'GPU', hint: 'Der entscheidende Faktor für flüssige Gaming-Performance, hohe Bildraten (FPS), Raytracing sowie schnelle Videorenderings.' },
  motherboard: { label: 'Mainboard', short: 'Mainboard', hint: 'Das Rückgrat deines Systems. Es verbindet alle Komponenten miteinander und bestimmt Anschlussmöglichkeiten sowie Zukunftssicherheit.' },
  ram: { label: 'Arbeitsspeicher', short: 'RAM', hint: 'Sorgt für schnelles Multitasking und nahtlose Abläufe. Je mehr und schnellerer RAM, desto flüssiger laufen moderne Spiele und viele offene Programme.' },
  storage: { label: 'Datenspeicher (SSD)', short: '1. Speicher', hint: 'Deine digitale Festplatte. Moderne NVMe-SSDs sorgen für sekundenschnelle Systemstarts und minimale Ladezeiten in Games.' },
  storage2: { label: '2. Datenspeicher (SSD)', short: '2. Speicher', hint: 'Zusätzlicher Speicherplatz für weitere Spiele, Projekte und Backups mit derselben rasanten NVMe-Geschwindigkeit.' },
  psu: { label: 'Netzteil (PSU)', short: 'Netzteil', hint: 'Die stabile Stromversorgung. Ein hochwertiges Netzteil garantiert Effizienz, leisen Betrieb und schützt deine Hardware vor Spannungsschwankungen.' },
  cooling: { label: 'CPU-Kühlung', short: 'Kühlung', hint: 'Hält deine CPU auch unter Volllast kühl. Verhindert Leistungsdrosselungen, verlängert die Lebensdauer und sorgt für einen leisen PC.' },
  fans: { label: 'Gehäuselüfter', short: 'Lüfter', hint: 'Sorgen für den perfekten Luftstrom (Airflow). Transportieren Hitze effizient nach außen und halten das gesamte System leise und kühl.' },
  os: { label: 'Betriebssystem', short: 'OS', hint: 'Dein PC wird komplett betriebsbereit mit aktuellem Windows 11 geliefert – inklusive Treiber-Updates und Funktionsprüfung.' },
};

function getRecommendedSelections(useCase, budget) {
  const map = {
    'Gaming': (b) => {
      if (b < 700) return { cpu: 'cpu-5', gpu: 'gpu-0', motherboard: 'mb-0', ram: 'ram-1', storage: 'ssd-1', psu: 'psu-0', case: 'case-0', cooling: 'air-1', os: 'os-1' };
      if (b < 900) return { cpu: 'cpu-1', gpu: 'gpu-1', motherboard: 'mb-1', ram: 'ram-1', storage: 'ssd-2', psu: 'psu-1', case: 'case-2', cooling: 'air-2', os: 'os-1' };
      if (b < 1400) return { cpu: 'cpu-1', gpu: 'gpu-3', motherboard: 'mb-1', ram: 'ram-2', storage: 'ssd-2', psu: 'psu-2', case: 'case-2', cooling: 'air-2', os: 'os-2' };
      return { cpu: 'cpu-2', gpu: 'gpu-4', motherboard: 'mb-1', ram: 'ram-2', storage: 'ssd-3', psu: 'psu-3', case: 'case-2', cooling: 'aio-1', os: 'os-2' };
    },
    'Creative Work': (b) => {
      if (b < 1300) return { cpu: 'cpu-2', gpu: 'gpu-3', motherboard: 'mb-1', ram: 'ram-2', storage: 'ssd-2', psu: 'psu-2', case: 'case-3', cooling: 'aio-1', os: 'os-2' };
      return { cpu: 'cpu-3', gpu: 'gpu-4', motherboard: 'mb-2', ram: 'ram-3', storage: 'ssd-3', psu: 'psu-3', case: 'case-3', cooling: 'aio-2', os: 'os-2' };
    },
    'Office/Home': () => ({ cpu: 'cpu-5', gpu: 'gpu-0', motherboard: 'mb-0', ram: 'ram-1', storage: 'ssd-1', psu: 'psu-0', case: 'case-1', cooling: 'air-1', os: 'os-2' }),
    'AI/Development': (b) => {
      if (b < 1600) return { cpu: 'cpu-3', gpu: 'gpu-3', motherboard: 'mb-2', ram: 'ram-3', storage: 'ssd-2', psu: 'psu-2', case: 'case-3', cooling: 'aio-1', os: 'os-1' };
      return { cpu: 'cpu-3', gpu: 'gpu-4', motherboard: 'mb-2', ram: 'ram-3', storage: 'ssd-3', psu: 'psu-3', case: 'case-3', cooling: 'aio-2', os: 'os-1' };
    },
    '': (b) => {
      if (b < 700) return { cpu: 'cpu-5', gpu: 'gpu-0', motherboard: 'mb-0', ram: 'ram-1', storage: 'ssd-1', psu: 'psu-0', case: 'case-0', cooling: 'air-1', os: 'os-1' };
      if (b < 900) return { cpu: 'cpu-1', gpu: 'gpu-1', motherboard: 'mb-1', ram: 'ram-1', storage: 'ssd-2', psu: 'psu-1', case: 'case-2', cooling: 'air-2', os: 'os-1' };
      if (b < 1400) return { cpu: 'cpu-1', gpu: 'gpu-3', motherboard: 'mb-1', ram: 'ram-2', storage: 'ssd-2', psu: 'psu-2', case: 'case-2', cooling: 'air-2', os: 'os-2' };
      return { cpu: 'cpu-2', gpu: 'gpu-4', motherboard: 'mb-1', ram: 'ram-2', storage: 'ssd-3', psu: 'psu-3', case: 'case-2', cooling: 'aio-1', os: 'os-2' };
    },
  };
  const fn = map[useCase] || map[''];
  return fn(budget);
}

function ConfigSidebar({
  step, budget, noBudget, selections, recommended, overBudget, currentTotal, budgetPercent,
  compatibility, onProceed, onAddToCart, configuratorData
}) {
  const selectedCase = selections.case
    ? configuratorData?.case?.find((c) => c.id === selections.case)
    : null;

  return (
    <div className="w-full lg:w-[320px] 2xl:w-[400px] shrink-0">
      <div className="bg-card border border-border rounded-xl p-5 2xl:p-6 flex flex-col gap-4 2xl:gap-5">
        <div>
          {selectedCase && selectedCase.image ? (
            <div className="w-full h-56 2xl:h-80 mb-3 2xl:mb-4 rounded-xl bg-secondary/40 border border-border flex items-center justify-center p-2">
              <img
                src={selectedCase.image}
                alt={selectedCase.name}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-full h-40 2xl:h-48 mb-3 2xl:mb-4 rounded-xl bg-secondary/20 border-2 border-border border-dashed flex flex-col items-center justify-center text-muted-foreground gap-2">
              <Package className="w-6 h-6 2xl:w-8 2xl:h-8 opacity-40" />
              <span className="text-[9px] 2xl:text-[10px] font-bold uppercase tracking-widest opacity-60">Gehäuse wählen</span>
            </div>
          )}

          <div className="text-[10px] 2xl:text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Deine Konfiguration</div>
          <div className={`text-2xl 2xl:text-4xl font-black ${overBudget ? 'text-destructive' : 'text-foreground'}`}>
            €{formatPrice(currentTotal)}
          </div>
          <div className="text-[12px] 2xl:text-sm text-muted-foreground mt-1">inkl. Zusammenbau & Test</div>
        </div>

        {!noBudget && (
          <div>
            <div className="flex justify-between text-[11px] 2xl:text-sm text-muted-foreground mb-1.5 2xl:mb-2">
              <span className="font-semibold">Budget €{formatPrice(budget)}</span>
              <span className={overBudget ? 'text-destructive font-bold' : ''}>
                {overBudget ? `+€${formatPrice(currentTotal - budget)} über` : `€${formatPrice(budget - currentTotal)} frei`}
              </span>
            </div>
            <div className="w-full bg-secondary rounded-full h-1.5 overflow-hidden">
              <div className={`h-1.5 rounded-full transition-all ${overBudget ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${budgetPercent}%` }} />
            </div>
          </div>
        )}

        <div>
          <div className="text-[10px] 2xl:text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 2xl:mb-3">Übersicht</div>
          <div className="space-y-1.5 2xl:space-y-2">
            {CATEGORY_ORDER.map((cat) => {
              const id = selections[cat];
              if (!id) return null;
              
              let item = null;
              if (cat === 'cooling') {
                  item = configuratorData?.air?.find(i => i.id === id) || configuratorData?.water?.find(i => i.id === id);
              } else {
                  item = configuratorData?.[cat]?.find(i => i.id === id);
              }

              if (!item) return null;
              return (
                <div key={cat} className="flex justify-between text-[11px] 2xl:text-sm gap-2">
                  <span className="text-muted-foreground shrink-0 w-20 2xl:w-24">{categoryMeta[cat]?.short}</span>
                  <span className="text-foreground/90 font-medium truncate text-right">{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-[10px] 2xl:text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 2xl:mb-3">Kompatibilität</div>
          {compatibility.issues.length > 0 ? (
            <div className="space-y-1.5 2xl:space-y-2">
              {compatibility.issues.map((iss, i) => (
                <div key={i} className="flex items-start gap-1.5 2xl:gap-2 text-[11px] 2xl:text-sm text-destructive">
                  <AlertTriangle className="w-3 h-3 2xl:w-4 2xl:h-4 shrink-0 mt-0.5" /> {iss}
                </div>
              ))}
            </div>
          ) : compatibility.isOk ? (
            <div className="flex items-center gap-1.5 2xl:gap-2 text-[11px] 2xl:text-sm text-emerald-600 font-medium">
              <CheckCircle2 className="w-3 h-3 2xl:w-4 2xl:h-4 shrink-0" /> Alle Komponenten kompatibel.
            </div>
          ) : (
            <div className="text-[11px] 2xl:text-sm text-muted-foreground">Wähle alle Pflichtkomponenten.</div>
          )}
        </div>

        {step === 'review' && (
          <button
            onClick={onAddToCart}
            disabled={!compatibility.isOk}
            className={`w-full py-2.5 2xl:py-3 mt-2 2xl:mt-3 rounded-lg font-bold text-sm 2xl:text-base flex items-center justify-center gap-2 transition-colors ${compatibility.isOk ? 'bg-accent text-accent-foreground hover:bg-accent/90' : 'bg-muted text-muted-foreground opacity-50 cursor-not-allowed'}`}
          >
            <ShoppingCart className="w-4 h-4 2xl:w-5 2xl:h-5" /> In den Warenkorb
          </button>
        )}
        {step === 'components' && (
          <button
            onClick={onProceed}
            disabled={!compatibility.isOk}
            className={`w-full py-2.5 2xl:py-3 mt-2 2xl:mt-3 rounded-lg font-bold text-sm 2xl:text-base transition-colors ${compatibility.isOk ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted text-muted-foreground opacity-50 cursor-not-allowed'}`}
          >
            Zur Überprüfung →
          </button>
        )}
      </div>
    </div>
  );
}

export default function Configurator() {
  const [configuratorData, setConfiguratorData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [step, setStep] = useState('usecase');
  const [useCase, setUseCase] = useState('');
  const [budget, setBudget] = useState(1200);
  const [noBudget, setNoBudget] = useState(false);

  const [subFilters, setSubFilters] = useState({ cpu: 'amd', gpu: 'nvidia' });
  const [fanQuantity, setFanQuantity] = useState(1);

  const [selections, setSelections] = useState(
    Object.fromEntries(CATEGORY_ORDER.map(k => [k, '']))
  );
  const [recommended, setRecommended] = useState({});
  const [activeCategory, setActiveCategory] = useState(CATEGORY_ORDER[0]);
  const [showErrors, setShowErrors] = useState(false);
  const [notification, setNotification] = useState(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const steps = [
    { id: 'usecase', label: 'Verwendungszweck' },
    { id: 'budget', label: 'Budget' },
    { id: 'components', label: 'Komponenten' },
    { id: 'review', label: 'Überprüfen' },
  ];
  const currentStepIdx = steps.findIndex(s => s.id === step);

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        const res = await fetch('/wp-json/pc-builder/v1/components');
        const data = await res.json();
        setConfiguratorData(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        showNotify('error', 'Fehler beim Laden der Komponenten.');
      }
    };
    fetchComponents();
  }, []);

  const showNotify = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSelectUseCase = (id) => { 
    setUseCase(id); 
    setStep('budget'); 
    scrollToTop(); 
  };
  
  const handleSkipUseCase = () => { 
    setUseCase(''); 
    setStep('budget'); 
    scrollToTop(); 
  };

  const handleGoToComponents = () => {
    setActiveCategory(CATEGORY_ORDER[0]);
    setShowErrors(false);
    if (noBudget) {
      setRecommended({});
      setSelections(Object.fromEntries(CATEGORY_ORDER.map(k => [k, ''])));
    } else {
      const recs = getRecommendedSelections(useCase, budget);
      setRecommended(recs);
      setSelections(prev => ({ ...prev, ...recs }));
    }
    setStep('components');
    scrollToTop();
  };

  const handleSelect = (category, id) => {
    // === LÜFTER RESET WENN GEHÄUSE ODER KÜHLER WECHSELT ===
    if ((category === 'case' || category === 'cooling') && selections[category] !== id && selections.fans !== '') {
        showNotify('error', 'Konfiguration geändert: Bitte überprüfe deine Lüfter-Auswahl.');
        setFanQuantity(1);
    }

    setSelections(prev => {
      const nextSelections = {
        ...prev,
        [category]: prev[category] === id ? '' : id
      };
      // Fans-Wert leeren, wenn sich das Gehäuse oder der Kühler ändert
      if ((category === 'case' || category === 'cooling') && prev[category] !== id) {
        nextSelections.fans = '';
      }
      return nextSelections;
    });

    if (category === 'fans') setFanQuantity(1);
  };

  const handleProceedToReview = () => {
    const missing = REQUIRED.filter(r => !selections[r] || selections[r] === '');
    if (missing.length > 0) {
      setShowErrors(true);
      setActiveCategory(missing[0]);
      showNotify('error', 'Bitte wähle alle markierten Komponenten.');
      scrollToTop();
      return;
    }
    if (compatibility.issues.length > 0) {
      showNotify('error', compatibility.issues[0]);
      scrollToTop();
      return;
    }
    setStep('review');
    scrollToTop();
  };

  const getPrice = (sels = selections) => {
    let total = 0;
    if (!configuratorData) return 0;
    Object.entries(sels).forEach(([cat, id]) => {
      if (id && id !== '') {
        let item = null;
        if (cat === 'cooling') {
            item = configuratorData?.air?.find(i => i.id === id) || configuratorData?.water?.find(i => i.id === id);
        } else {
            item = configuratorData?.[cat]?.find(i => i.id === id);
        }
        
        if (item && !isNaN(item.price)) {
            if (cat === 'fans') {
                total += item.price * fanQuantity;
            } else {
                total += item.price;
            }
        }
      }
    });
    return total;
  };

  const compatibility = useMemo(() => {
    const issues = [];
    if (!configuratorData) return { issues, isOk: false };

    let powerDraw = 0;
    const cpu = configuratorData.cpu?.find(c => c.id === selections.cpu);
    const mb = configuratorData.motherboard?.find(m => m.id === selections.motherboard);
    const gpu = configuratorData.gpu?.find(g => g.id === selections.gpu);
    const psu = configuratorData.psu?.find(p => p.id === selections.psu);

    if (cpu && mb && cpu.socket && mb.socket && cpu.socket.toLowerCase() !== mb.socket.toLowerCase()) {
      issues.push(`CPU (${cpu.socket}) ≠ Mainboard (${mb.socket}).`);
    }
    if (cpu) powerDraw += (cpu.power || 0);
    if (gpu) powerDraw += (gpu.power || 0);
    powerDraw += 100;

    const ps = psu ? (psu.wattage || 0) : 0;
    if (ps > 0 && powerDraw > ps * 0.9) issues.push(`Netzteil (${ps}W) zu schwach (ca. ${powerDraw}W benötigt).`);

    const allRequired = REQUIRED.every(r => selections[r] && selections[r] !== '');
    return { issues, isOk: issues.length === 0 && allRequired };
  }, [selections, configuratorData]);

  const currentTotal = getPrice() + 99; // 99 is the build fee
  const budgetPercent = Math.min(100, Math.round((currentTotal / budget) * 100));
  const overBudget = !noBudget && currentTotal > budget;

  const selectedGpu = configuratorData?.gpu?.find(g => g.id === selections.gpu);
  
  const [fpsResolution, setFpsResolution] = useState('1440p');

  const FpsDisplay = () => {
    const gpuFps = selectedGpu && selectedGpu.fpsIndex !== undefined
      ? gpuFpsTable[fpsResolution]?.[selectedGpu.fpsIndex]
      : null;

    if (!gpuFps) return null;

    return (
      <div className="mt-6 2xl:mt-8">
        <div className="flex items-center justify-between mb-4 2xl:mb-6">
          <div className="flex items-center gap-1.5 2xl:gap-2">
            <Zap className="w-4 h-4 2xl:w-5 2xl:h-5 text-primary" />
            <span className="font-bold text-xs lg:text-sm 2xl:text-base tracking-wide text-foreground">Geschätzte FPS</span>
          </div>
          
          <div className="flex bg-secondary/50 rounded-lg p-1 border border-border">
            {['1080p', '1440p', '4K'].map(res => (
              <button
                key={res}
                onClick={() => setFpsResolution(res)}
                className={`px-3 py-1 2xl:px-4 2xl:py-1.5 rounded-md text-[10px] 2xl:text-xs font-bold transition-all ${
                  fpsResolution === res 
                    ? 'bg-background text-foreground shadow-sm border border-border/50' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {res}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 2xl:gap-x-8 gap-y-4 2xl:gap-y-6">
          {gpuFps.map((r, i) => {
            const maxF = r.maxFps || 240;
            const percentage = Math.min(100, (r.fps / maxF) * 100);

            return (
              <div key={i} className="flex items-center gap-3 2xl:gap-4">
                <img src={r.logo} alt={r.game} className="w-8 h-8 2xl:w-12 2xl:h-12 rounded-lg bg-secondary p-1.5 object-contain shrink-0" />

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-end mb-1 2xl:mb-1.5">
                    <span className="text-[9px] 2xl:text-[11px] font-bold text-muted-foreground uppercase tracking-wider">{r.game}</span>
                    <span className="text-xs 2xl:text-sm font-black text-foreground">{r.fps} <span className="text-[8px] 2xl:text-[10px] font-normal opacity-60">FPS</span></span>
                  </div>

                  <div className="w-full h-1.5 2xl:h-2 bg-secondary rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="h-full bg-primary rounded-full"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleAddToCart = async () => {
    if (compatibility.issues.length > 0) {
      showNotify('error', 'Bitte behebe die Kompatibilitätsprobleme.');
      return;
    }
    if (REQUIRED.some(r => !selections[r])) {
      showNotify('error', 'Bitte wähle alle Pflichtkomponenten.');
      return;
    }

    const selectedComponentIds = [];
    Object.entries(selections).forEach(([cat, id]) => {
        if (id && id !== '') {
            if (cat === 'fans') {
                for (let i = 0; i < fanQuantity; i++) {
                    selectedComponentIds.push(id);
                }
            } else {
                selectedComponentIds.push(id);
            }
        }
    });

    try {
      showNotify('success', 'Wird zum Warenkorb hinzugefügt...');

      // Nutze FormData für den klassischen WordPress AJAX Endpunkt
      const formData = new FormData();
      formData.append('action', 'pc_builder_add_to_cart');
      selectedComponentIds.forEach(id => {
         formData.append('product_ids[]', id); // Fügt jedes Element als Array-Teil hinzu
      });

      // Wir senden den Request an admin-ajax.php statt an wp-json!
      const response = await fetch('/wp-admin/admin-ajax.php', {
        method: 'POST',
        body: formData // Body ist jetzt FormData, kein JSON mehr
      });

      const data = await response.json();

      if (data.success) {
        showNotify('success', 'Dein Custom PC ist im Warenkorb.');
        window.location.href = data.data.redirect || '/cart'; // ACHTUNG: Bei wp_send_json_success steckt das Resultat oft in data.data
      } else {
        showNotify('error', data.data || 'Fehler beim Hinzufügen zum Warenkorb.');
      }
    } catch (error) {
      console.error("Cart API Error:", error);
      showNotify('error', 'Netzwerkfehler beim Hinzufügen.');
    }
  };

  if (isLoading || !configuratorData) {
    return (
      <div className="w-full min-h-[calc(100vh-3rem)] flex flex-col items-center justify-center bg-background gap-4">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
        <h2 className="font-bold text-xl text-foreground">Lade System-Komponenten...</h2>
      </div>
    );
  }

  const validCount = REQUIRED.filter(r => selections[r] && selections[r] !== '').length;

  return (
    <div className="font-sans bg-background min-h-[calc(100vh-3rem)] flex flex-col relative w-full overflow-x-hidden">

      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -20, x: "-50%" }}
            className={`fixed top-8 left-1/2 z-50 px-5 py-3 2xl:px-6 2xl:py-4 rounded-lg shadow-xl text-sm 2xl:text-base font-bold flex items-center gap-2 2xl:gap-3 w-11/12 max-w-md md:w-auto ${notification.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-emerald-500 text-white'}`}
          >
            {notification.type === 'error' ? <AlertTriangle className="w-5 h-5 2xl:w-6 2xl:h-6 shrink-0" /> : <CheckCircle2 className="w-5 h-5 2xl:w-6 2xl:h-6 shrink-0" />}
            {notification.text}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-card border-b border-border sticky z-40 w-full">
        <div className="w-full max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 py-2.5 2xl:py-3 flex flex-wrap justify-start items-center gap-2.5 2xl:gap-6">
          {steps.map((s, i) => (
            <div key={s.id} className="flex items-center gap-1.5 2xl:gap-3 shrink-0">
              <div className={`w-6 h-6 2xl:w-8 2xl:h-8 rounded-full flex items-center justify-center font-bold text-xs 2xl:text-sm transition-colors ${step === s.id ? 'bg-primary text-primary-foreground' : i < currentStepIdx ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'
                }`}>
                {i < currentStepIdx ? <Check className="w-3 h-3 2xl:w-4 2xl:h-4" /> : i + 1}
              </div>
              <span className={`font-semibold text-xs 2xl:text-sm hidden lg:block ${step === s.id ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
              {i < steps.length - 1 && <ChevronRight className="w-3 h-3 2xl:w-4 2xl:h-4 text-border mx-1" />}
            </div>
          ))}
        </div>
      </div>

      <div className="w-full max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 py-6 2xl:py-8 flex flex-col lg:flex-row justify-center gap-6 2xl:gap-8 items-start">
        <div className="w-full flex-1 min-w-0">
          <AnimatePresence mode="wait">

            {step === 'usecase' && (
              <motion.div key="usecase" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="max-w-3xl mx-auto">
                <h1 className="text-2xl lg:text-3xl 2xl:text-4xl font-black mb-1 2xl:mb-2">
                  <span className="font-light">System </span>Konfigurator.
                </h1>
                <p className="text-muted-foreground text-sm 2xl:text-base mb-6 2xl:mb-8">Wofür nutzt du den PC primär?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 2xl:gap-4">
                  {[
                    { id: 'Gaming', icon: Gamepad2, desc: 'Maximale FPS, hohe Bildwiederholrate' },
                    { id: 'Creative Work', icon: Monitor, desc: 'Videoschnitt, 3D-Rendering, Adobe Suite' },
                    { id: 'Office/Home', icon: Briefcase, desc: 'Surfen, Office-Anwendungen' },
                    { id: 'AI/Development', icon: Server, desc: 'Compilieren, Machine Learning, Docker' },
                  ].map(uc => (
                    <button
                      key={uc.id}
                      onClick={() => handleSelectUseCase(uc.id)}
                      className={`flex items-center gap-3 2xl:gap-4 p-4 lg:p-4 2xl:p-4 rounded-xl border-2 transition-all text-left group ${useCase === uc.id ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50 hover:bg-secondary/40'
                        }`}
                    >
                      <div className="w-10 h-10 2xl:w-12 2xl:h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                        <uc.icon className="w-5 h-5 2xl:w-6 2xl:h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm 2xl:text-base mb-0.5 2xl:mb-1">{uc.id}</h3>
                        <p className="text-xs 2xl:text-sm text-muted-foreground">{uc.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-6 2xl:mt-8 flex justify-center w-full">
                  <button onClick={handleSkipUseCase} className="flex items-center gap-2 text-xs 2xl:text-sm text-muted-foreground hover:text-foreground border border-border/60 rounded-lg px-4 py-2.5 2xl:px-5 2xl:py-3 transition-colors w-full sm:w-auto justify-center">
                    <SkipForward className="w-3 h-3 2xl:w-4 2xl:h-4 shrink-0" /> Weiter ohne Profil — ich wähle Komponenten selbst
                  </button>
                </div>
              </motion.div>
            )}

            {step === 'budget' && (
              <motion.div key="budget" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="max-w-3xl mx-auto">
                <h1 className="text-2xl lg:text-3xl 2xl:text-4xl font-black mb-1 2xl:mb-2">
                  <span className="font-light">Wie hoch ist dein </span>Budget?
                </h1>
                <p className="text-muted-foreground text-sm 2xl:text-base mb-6 2xl:mb-8">Nur für den PC — ohne Monitor und Peripherie.</p>

                <div className={`bg-card border border-border p-5 lg:p-6 2xl:p-8 rounded-xl mb-5 2xl:mb-6 text-center ${noBudget ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                  <div className="text-3xl lg:text-4xl 2xl:text-5xl font-black text-primary mb-2 2xl:mb-3">ca. €{formatPrice(budget)}</div>
                  <p className="text-xs 2xl:text-sm text-muted-foreground mb-6 2xl:mb-8">
                    {budget < 700 ? 'Budget-Einstieg — solides 1080p Gaming oder Office'
                      : budget < 900 ? 'Einstieg — flüssiges 1080p Gaming'
                        : budget < 1400 ? 'Mittelklasse — flüssiges 1440p Gaming'
                          : budget < 2000 ? 'Enthusiast — 4K Gaming oder Creative Pro'
                            : 'High-End — keine Kompromisse'}
                  </p>
                  <input
                    type="range" min="500" max="4000" step="50" value={budget}
                    onChange={e => setBudget(Number(e.target.value))}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary mb-6 2xl:mb-8"
                    disabled={noBudget}
                  />
                  <div className="flex flex-wrap justify-center gap-2 2xl:gap-3">
                    {[600, 800, 1000, 1500, 2000, 2500].map(val => (
                      <button key={val} onClick={() => { setBudget(val); setNoBudget(false); }}
                        className={`px-3 py-1.5 2xl:px-4 2xl:py-2 rounded-full text-xs 2xl:text-sm font-semibold border transition-colors ${!noBudget && budget === val ? 'bg-primary border-primary text-primary-foreground' : 'border-border hover:border-primary/50'}`}
                      >
                        €{formatPrice(val)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-5 2xl:mb-6">
                  <button
                    onClick={() => setNoBudget(b => !b)}
                    className={`w-full flex items-center justify-between px-5 py-3 2xl:px-6 2xl:py-4 rounded-xl border-2 transition-all text-left ${noBudget ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'
                      }`}
                  >
                    <div className="pr-4">
                      <div className="font-bold text-sm 2xl:text-base mb-1">Kein Budget festlegen</div>
                      <div className="text-xs 2xl:text-sm text-muted-foreground">Ich wähle alle Teile frei — ohne Vorauswahl.</div>
                    </div>
                    <div className={`w-5 h-5 2xl:w-6 2xl:h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${noBudget ? 'border-primary bg-primary' : 'border-border'}`}>
                      {noBudget && <Check className="w-3 h-3 2xl:w-4 2xl:h-4 text-primary-foreground" />}
                    </div>
                  </button>
                </div>

                {useCase && !noBudget && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 2xl:p-4 mb-5 2xl:mb-6 flex items-start sm:items-center gap-2.5 2xl:gap-3">
                    <TrendingUp className="w-4 h-4 2xl:w-5 2xl:h-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
                    <p className="text-xs 2xl:text-sm"><span className="font-bold">{useCase}</span> mit <span className="font-bold">€{formatPrice(budget)}</span> — wir wählen die optimalen Komponenten vor.</p>
                  </div>
                )}

                <div className="flex justify-between items-center mt-6 2xl:mt-8">
                  <button onClick={() => { setStep('usecase'); scrollToTop(); }} className="px-3 py-2 2xl:px-4 2xl:py-2 text-xs 2xl:text-sm font-semibold text-muted-foreground hover:text-foreground">← Zurück</button>
                  <button onClick={handleGoToComponents} className="bg-primary text-primary-foreground px-5 py-2.5 2xl:px-6 2xl:py-3 rounded-lg font-bold text-xs 2xl:text-sm shadow-sm hover:bg-primary/90 flex items-center gap-2">
                    Komponenten wählen <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 'components' && (
              <motion.div key="components" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 2xl:mb-6 gap-3 2xl:gap-4">
                  <div>
                    <h1 className="text-2xl lg:text-3xl 2xl:text-4xl font-black mb-1">
                      <span className="font-light">System </span>Konfigurator.
                    </h1>
                    <p className="text-xs 2xl:text-sm text-muted-foreground">
                      {noBudget
                        ? <>Wähle alle Komponenten frei nach deinen Wünschen.</>
                        : useCase
                          ? <>Empfehlung für <strong>{useCase}</strong> mit <strong>€{formatPrice(budget)}</strong> vorgewählt.</>
                          : <>Budget <strong>€{formatPrice(budget)}</strong> — passe nach Wunsch an.</>}
                    </p>
                  </div>
                  {!noBudget && (
                    <button
                      onClick={() => { setSelections(prev => ({ ...prev, ...recommended })); }}
                      className="text-[10px] 2xl:text-xs font-semibold text-primary hover:underline border border-primary/25 rounded-lg px-3 py-1.5 2xl:px-4 2xl:py-2 hover:bg-primary/5 transition-colors shrink-0 w-full sm:w-auto text-center"
                    >
                      Zurücksetzen
                    </button>
                  )}
                </div>

                <div className="mb-4 2xl:mb-6">
                  <div className="flex justify-between text-[10px] 2xl:text-xs text-muted-foreground mb-1.5 2xl:mb-2">
                    <span>{validCount} / {REQUIRED.length} Pflichtkomponenten</span>
                    {compatibility.isOk && <span className="text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3 2xl:w-4 2xl:h-4" /> Kompatibel</span>}
                  </div>
                  <div className="w-full bg-secondary rounded-full h-1.5 overflow-hidden">
                    <div className="h-1.5 rounded-full bg-primary transition-all duration-300" style={{ width: `${(validCount / REQUIRED.length) * 100}%` }} />
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 2xl:gap-2 mb-5 2xl:mb-6">
                  {CATEGORY_ORDER.map((catKey) => {
                    const meta = categoryMeta[catKey];
                    // Validierungs-Prüfung, ob überhaupt ein Wert da ist
                    const isSelected = selections[catKey] && selections[catKey] !== '';
                    const isRequired = REQUIRED.includes(catKey);
                    const isMissing = isRequired && !isSelected && showErrors;
                    const isActive = activeCategory === catKey;

                    return (
                      <button
                        key={catKey}
                        onClick={() => { setActiveCategory(catKey); scrollToTop(); }}
                        className={`flex items-center gap-1.5 2xl:gap-2 px-3 py-1.5 2xl:px-4 2xl:py-2 rounded-full text-[11px] 2xl:text-sm font-semibold whitespace-nowrap border transition-all shrink-0 ${isActive
                          ? 'bg-primary text-primary-foreground border-primary'
                          : isMissing
                            ? 'border-destructive text-destructive bg-destructive/5'
                            : isSelected
                              ? 'border-primary/40 text-primary bg-primary/5'
                              : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground bg-card'
                          }`}
                      >
                        {isSelected && !isActive ? <Check className="w-3 h-3 2xl:w-4 2xl:h-4 shrink-0" /> : null}
                        {isMissing ? <AlertTriangle className="w-3 h-3 2xl:w-4 2xl:h-4 shrink-0" /> : null}
                        {meta.short}
                      </button>
                    );
                  })}
                </div>

                {(() => {
                  const catKey = activeCategory;
                  const meta = categoryMeta[catKey] || { label: catKey, hint: '' };
                  const currentIdx = CATEGORY_ORDER.indexOf(catKey);
                  const nextCatKey = currentIdx < CATEGORY_ORDER.length - 1 ? CATEGORY_ORDER[currentIdx + 1] : null;
                  const nextMeta = nextCatKey ? categoryMeta[nextCatKey] : null;
                  const hasSelection = !!selections[catKey];
                  const isCoolingCat = catKey === 'cooling';

                  const selectedCpuSocket = selections.cpu ? configuratorData?.cpu?.find(c => c.id === selections.cpu)?.socket?.toLowerCase() : null;
                  const selectedMbSocket = selections.motherboard ? configuratorData?.motherboard?.find(m => m.id === selections.motherboard)?.socket?.toLowerCase() : null;

                  let displayItems = [];
                  if (isCoolingCat) {
                      displayItems = [...(configuratorData?.air || []), ...(configuratorData?.water || [])];
                  } else {
                      displayItems = [...(configuratorData?.[catKey] || [])];
                  }
                  
                  displayItems.sort((a, b) => (a.price || 0) - (b.price || 0));

                  if (catKey === 'motherboard' && selectedCpuSocket) {
                    displayItems = displayItems.filter(i => i.socket && i.socket.toLowerCase() === selectedCpuSocket);
                  }

                  if (catKey === 'cpu') {
                    displayItems = displayItems.filter(i => {
                      const name = (i.name || '').toLowerCase();
                      return subFilters.cpu === 'amd'
                        ? (name.includes('ryzen') || name.includes('amd'))
                        : (name.includes('core') || name.includes('intel'));
                    });
                  } else if (catKey === 'gpu') {
                    displayItems = displayItems.filter(i => {
                      const name = (i.name || '').toLowerCase();
                      return subFilters.gpu === 'nvidia'
                        ? (name.includes('rtx') || name.includes('geforce') || name.includes('nvidia') || name.includes('gtx'))
                        : (name.includes('rx') || name.includes('radeon') || name.includes('amd'));
                    });
                  }

                  const coolingAir = isCoolingCat ? displayItems.filter(i => configuratorData?.air?.some(a => a.id === i.id)) : [];
                  const coolingAio = isCoolingCat ? displayItems.filter(i => configuratorData?.water?.some(w => w.id === i.id)) : [];

                  // === LOGIK FÜR DIE LÜFTER INFOBOX ===
                  const currentCase = configuratorData?.case?.find(c => c.id === selections.case);
                  const currentCooler = configuratorData?.water?.find(c => c.id === selections.cooling) || configuratorData?.air?.find(c => c.id === selections.cooling);
                  
                  const caseFansIncluded = parseInt(currentCase?.fansCase) || 0;
                  const caseFansExtra = parseInt(currentCase?.fansCaseExtra) || 0;
                  const coolerFans = parseInt(currentCooler?.fansCooler) || 0;
                  
                  const totalCaseSlots = caseFansIncluded + caseFansExtra;
                  const baseFreeSlots = Math.max(0, caseFansExtra - coolerFans);
                  const maxPossibleFans = baseFreeSlots + caseFansIncluded;
                  
                  const currentSelectedFans = selections.fans && selections.fans !== '' ? fanQuantity : 0;
                  const remainingFreeSlots = Math.max(0, baseFreeSlots - currentSelectedFans);

                  const getComponentTags = (item) => {
                    let isBestValue = false;
                    let isUpgrade = false;
                    let isSavings = false;

                    const baseId = noBudget ? DEFAULT_NO_BUDGET_REC[catKey] : recommended[catKey];
                    const anchorId = selections[catKey] || baseId;
                    
                    let anchorItem = null;
                    if (isCoolingCat) {
                        anchorItem = configuratorData?.air?.find(i => i.id === anchorId) || configuratorData?.water?.find(i => i.id === anchorId);
                    } else {
                        anchorItem = configuratorData?.[catKey]?.find(i => i.id === anchorId);
                    }

                    let overBudget = false;
                    if (!noBudget && selections[catKey] !== item.id) {
                      const priceDiff = (item.price || 0) - (anchorItem ? (anchorItem.price || 0) : 0);
                      overBudget = (currentTotal + priceDiff) > budget;
                    }

                    if (['case', 'cooling', 'fans', 'os'].includes(catKey)) {
                      return { isBestValue: false, isUpgrade: false, isSavings: false, overBudget };
                    }

                    if (catKey === 'storage') {
                      isBestValue = (item.id === 'ssd-1');
                    } else if (catKey === 'psu') {
                      let systemTDP = 100;
                      const selCpu = configuratorData?.cpu?.find(c => c.id === (selections.cpu || (noBudget ? DEFAULT_NO_BUDGET_REC.cpu : recommended.cpu)));
                      const selGpu = configuratorData?.gpu?.find(g => g.id === (selections.gpu || (noBudget ? DEFAULT_NO_BUDGET_REC.gpu : recommended.gpu)));
                      if (selCpu) systemTDP += (selCpu.power || 0);
                      if (selGpu) systemTDP += (selGpu.power || 0);

                      const sortedPSUs = [...(configuratorData?.psu || [])].sort((a, b) => (a.wattage || 0) - (b.wattage || 0));
                      const bestValuePSU = sortedPSUs.find(p => (p.wattage || 0) >= systemTDP + 150);
                      const upgradePSU = sortedPSUs.find(p => (p.wattage || 0) >= systemTDP + 300);

                      isBestValue = bestValuePSU && item.id === bestValuePSU.id;
                      isUpgrade = upgradePSU && item.id === upgradePSU.id && item.id !== bestValuePSU?.id;
                    } else {
                      isBestValue = (item.id === baseId);
                    }

                    if (['ram', 'storage'].includes(catKey)) {
                    } else if (catKey === 'storage2') {
                      if (item.price > 0) isUpgrade = true; 
                    } else if (catKey === 'gpu') {
                      let brandAnchorItem = anchorItem;

                      if (anchorItem) {
                        const isAnchorNvidia = anchorItem.name.toLowerCase().includes('rtx') || anchorItem.name.toLowerCase().includes('nvidia');
                        if ((subFilters.gpu === 'amd' && isAnchorNvidia) || (subFilters.gpu === 'nvidia' && !isAnchorNvidia)) {
                           brandAnchorItem = [...displayItems].sort((a,b) => Math.abs((a.price || 0) - (anchorItem.price || 0)))[0];
                        }
                      }

                      if (brandAnchorItem) {
                        const anchorIdx = displayItems.findIndex(i => i.id === brandAnchorItem.id);
                        if (anchorIdx !== -1) {
                           const savingsOptions = displayItems.slice(0, anchorIdx).filter(i => (i.fpsIndex || 0) >= (brandAnchorItem.fpsIndex || 0));
                           if (savingsOptions.length > 0 && item.id === savingsOptions[savingsOptions.length - 1].id) {
                               isSavings = true;
                           }
                           const upgradeOptions = displayItems.slice(anchorIdx + 1).filter(i => (i.fpsIndex || 0) > (brandAnchorItem.fpsIndex || 0));
                           if (upgradeOptions.length > 0 && item.id === upgradeOptions[0].id) {
                               isUpgrade = true;
                           }
                        }
                      }
                    } else if (catKey === 'cpu') {
                      let brandAnchorItem = anchorItem;

                      if (anchorItem) {
                        const isAnchorAmd = anchorItem.name.toLowerCase().includes('ryzen') || anchorItem.name.toLowerCase().includes('amd');
                        if ((subFilters.cpu === 'intel' && isAnchorAmd) || (subFilters.cpu === 'amd' && !isAnchorAmd)) {
                           brandAnchorItem = [...displayItems].sort((a,b) => Math.abs((a.price || 0) - (anchorItem.price || 0)))[0];
                        }
                      }

                      if (brandAnchorItem) {
                        const anchorIdx = displayItems.findIndex(i => i.id === brandAnchorItem.id);
                        if (anchorIdx !== -1) {
                          if (anchorIdx > 0 && item.id === displayItems[anchorIdx - 1].id) isSavings = true;
                          if (anchorIdx < displayItems.length - 1 && item.id === displayItems[anchorIdx + 1].id) isUpgrade = true;
                        }
                      }
                    } else if (catKey === 'motherboard') {
                      if (anchorItem && item.socket === anchorItem.socket) {
                        const sameSocketItems = displayItems.filter(i => i.socket === anchorItem.socket);
                        const anchorIdx = sameSocketItems.findIndex(i => i.id === anchorItem.id);
                        if (anchorIdx !== -1) {
                          if (anchorIdx > 0 && item.id === sameSocketItems[anchorIdx - 1].id) isSavings = true;
                          if (anchorIdx < sameSocketItems.length - 1 && item.id === sameSocketItems[anchorIdx + 1].id) isUpgrade = true;
                        }
                      }
                    }

                    if (overBudget) isUpgrade = false;
                    return { isBestValue, isUpgrade, isSavings, overBudget };
                  };

                  const renderComponentItem = (item, forceWater = false) => {
                    const isItemSelected = selections[catKey] === item.id;
                    const tags = getComponentTags(item);
                    
                    const baseId = noBudget ? DEFAULT_NO_BUDGET_REC[catKey] : recommended[catKey];
                    const anchorId = selections[catKey] || baseId;
                    
                    let anchorPrice = 0;
                    if (isCoolingCat) {
                        anchorPrice = configuratorData?.air?.find(x => x.id === anchorId)?.price || configuratorData?.water?.find(x => x.id === anchorId)?.price || 0;
                    } else {
                        anchorPrice = configuratorData?.[catKey]?.find(x => x.id === anchorId)?.price || 0;
                    }

                    const priceDiff = (item.price || 0) - anchorPrice;
                    const isWatercooler = forceWater || (isCoolingCat && configuratorData?.water?.some(w => w.id === item.id));

                    const isSocketIncompatible = catKey === 'cpu' && selectedMbSocket && item.socket && item.socket.toLowerCase() !== selectedMbSocket;

                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          if (isSocketIncompatible) {
                            setSelections(prev => ({ ...prev, cpu: item.id, motherboard: '' }));
                            showNotify('error', 'Mainboard entfernt, da ein anderer CPU-Sockel gewählt wurde.');
                          } else {
                            handleSelect(catKey, item.id);
                          }
                        }}
                        className={`relative mt-3 2xl:mt-4 flex items-center gap-3 2xl:gap-4 p-3 lg:p-4 2xl:p-5 rounded-xl border-2 text-left transition-all ${
                          isSocketIncompatible
                          ? 'opacity-40 grayscale border-border bg-muted hover:bg-secondary/50'
                          : isItemSelected && tags.isBestValue
                            ? 'border-accent bg-accent/5 shadow-sm border-4'
                            : isItemSelected
                              ? 'border-primary bg-primary/5 shadow-sm border-4'
                              : tags.overBudget
                                ? 'border-border bg-card opacity-60'
                                : 'border-border bg-card hover:border-primary/50 hover:bg-secondary/30'
                          }`}
                      >
                        <div className="absolute -top-2 left-3 2xl:-top-2.5 2xl:left-4 flex gap-1.5 z-10">
                          {isSocketIncompatible ? (
                            <span className="inline-flex items-center gap-1 bg-destructive text-white text-[8px] 2xl:text-[9px] font-bold uppercase tracking-widest px-2 2xl:px-2.5 py-0.5 rounded-full shadow-sm">
                              <ShieldAlert className="w-2 h-2 2xl:w-2.5 2xl:h-2.5" /> Falscher Sockel
                            </span>
                          ) : (
                            <>
                              {tags.isBestValue && (
                                <span className="inline-flex items-center gap-1 bg-accent text-[8px] 2xl:text-[9px] font-bold uppercase tracking-widest px-2 2xl:px-2.5 py-0.5 rounded-full shadow-sm">
                                  <Star className="w-2 h-2 2xl:w-2.5 2xl:h-2.5" fill="currentColor" /> Best Value
                                </span>
                              )}
                              {tags.isUpgrade && (
                                <span className="inline-flex items-center gap-1 bg-rose-500 text-white text-[8px] 2xl:text-[9px] font-bold uppercase tracking-widest px-2 2xl:px-2.5 py-0.5 rounded-full shadow-sm">
                                  <TrendingUp className="w-2 h-2 2xl:w-2.5 2xl:h-2.5" /> Upgrade
                                </span>
                              )}
                              {tags.isSavings && (
                                <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[8px] 2xl:text-[9px] font-bold uppercase tracking-widest px-2 2xl:px-2.5 py-0.5 rounded-full shadow-sm">
                                  <Check className="w-2 h-2 2xl:w-2.5 2xl:h-2.5" /> Spar-Tipp
                                </span>
                              )}
                            </>
                          )}
                        </div>

                        {item.image && (
                          <div className={`${getResponsiveImageWidth(catKey, isWatercooler)} h-10 lg:h-12 2xl:h-16 shrink-0 flex items-center justify-start`}>
                            <img src={item.image} alt={item.name} className="w-full h-full object-contain object-left" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0 pr-2">
                          <h4 className={`font-bold text-xs lg:text-sm 2xl:text-base leading-snug ${isItemSelected && tags.isBestValue ? 'text-accent' : isItemSelected ? 'text-primary' : ''}`}>
                            {item.name || "Komponente"}
                          </h4>
                          <p className="text-[10px] 2xl:text-xs text-muted-foreground mt-0.5">
                            {item.specs || "Spezifikationen"}
                          </p>
                          {/* NEU: Lüfter-Zähler Buttons */}
                          {catKey === 'fans' && isItemSelected && (
                            <div className="mt-2 flex items-center gap-3">
                                <button 
                                    onClick={(e) => { e.stopPropagation(); setFanQuantity(Math.max(1, fanQuantity - 1)); }}
                                    className="w-6 h-6 flex items-center justify-center bg-secondary rounded-md text-foreground font-bold hover:bg-border transition-colors"
                                >-</button>
                                <span className="font-bold text-xs">{fanQuantity}x</span>
                                <button 
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      if (!currentCase) {
                                          showNotify('error', 'Bitte wähle zuerst ein Gehäuse.');
                                      } else if (fanQuantity < maxPossibleFans) {
                                          setFanQuantity(fanQuantity + 1); 
                                      } else {
                                          showNotify('error', `Limit erreicht: Es passen maximal ${maxPossibleFans} Lüfter ins Gehäuse.`);
                                      }
                                    }}
                                    className={`w-6 h-6 flex items-center justify-center rounded-md font-bold transition-colors ${
                                        currentCase && fanQuantity >= maxPossibleFans 
                                        ? 'bg-secondary/40 text-muted-foreground cursor-not-allowed' 
                                        : 'bg-secondary text-foreground hover:bg-border'
                                    }`}
                                >+</button>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col items-end shrink-0">
                          {(!hasSelection || isItemSelected) ? (
                            <span className="font-black text-xs lg:text-sm 2xl:text-base leading-none">
                              {item.price > 0 ? `€${formatPrice(item.price)}` : 'Gratis'}
                            </span>
                          ) : (
                            <span className={`font-black text-xs lg:text-sm 2xl:text-base leading-none ${priceDiff > 0 ? 'text-rose-500' : priceDiff < 0 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                              {priceDiff > 0 ? `+ €${formatPrice(priceDiff)}` : priceDiff < 0 ? `- €${formatPrice(Math.abs(priceDiff))}` : '± €0,00'}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  };

                  return (
                    <AnimatePresence mode="wait">
                      <motion.div key={catKey} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
                        <div className="flex items-center justify-between mb-2 2xl:mb-3">
                          <h2 className="font-bold text-base lg:text-lg 2xl:text-xl">{meta.label}</h2>
                        </div>
                        {meta.hint && (
                          <p className="text-[10px] 2xl:text-sm text-muted-foreground mb-4 2xl:mb-6">{meta.hint}</p>
                        )}

                        {/* === FAN INFO BOX === */}
                        {catKey === 'fans' && (
                          <div className="mb-4 2xl:mb-6">
                              <div className="flex items-center gap-2 font-bold text-xs 2xl:text-sm mb-2">
                                 <Zap className="w-4 h-4 text-primary" /> Gehäuse Lüfter Info
                              </div>
                              {!currentCase ? (
                                  <p className="text-[10px] 2xl:text-xs text-muted-foreground">Wähle zuerst ein Gehäuse aus, um die Lüfter-Slots zu berechnen.</p>
                              ) : (
                                  <>
                                      <div className="text-[10px] 2xl:text-xs text-muted-foreground flex flex-col gap-1">
                                          <p>• Maximale Lüfterplätze im Gehäuse: <strong>{totalCaseSlots}</strong></p>
                                          <p>• Davon vorinstallierte Gehäuselüfter: <strong>{caseFansIncluded}</strong></p>
                                          <p>• Belegte Plätze durch CPU-Kühler: <strong>{coolerFans}</strong></p>
                                          <p className={`${remainingFreeSlots > 0 ? 'text-emerald-500' : 'text-orange-500'} font-semibold mt-1`}>
                                              • Freie Plätze: <strong>{remainingFreeSlots}</strong>
                                          </p>
                                      </div>

                                      {/* Dynamische Nachrichten basierend auf der Menge */}
                                      {currentSelectedFans === baseFreeSlots && baseFreeSlots < maxPossibleFans && (
                                           <p className="mt-2 text-[10px] 2xl:text-xs text-blue-500 flex items-start gap-1.5">
                                              <Info className="w-3 h-3 shrink-0 mt-0.5" />
                                              <span>Alle freien Plätze sind belegt. Du kannst aber noch bis zu {caseFansIncluded} weitere Lüfter auswählen, um die einfachen Standard-Gehäuselüfter aufzuwerten/zu ersetzen.</span>
                                           </p>
                                      )}
                                      {currentSelectedFans > baseFreeSlots && currentSelectedFans <= maxPossibleFans && (
                                           <p className="mt-2 text-[10px] 2xl:text-xs text-orange-500 flex items-start gap-1.5">
                                              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                                              <span>Du hast mehr Lüfter gewählt als freie Plätze vorhanden sind. Es werden {currentSelectedFans - baseFreeSlots} inkludierte Gehäuselüfter ausgebaut und durch deine neuen ersetzt.</span>
                                           </p>
                                      )}
                                      {currentSelectedFans > maxPossibleFans && (
                                           <p className="mt-2 text-[10px] 2xl:text-xs text-destructive flex items-start gap-1.5">
                                              <ShieldAlert className="w-3 h-3 shrink-0 mt-0.5" />
                                              <span>Limit überschritten! Dein Gehäuse unterstützt inklusive Austausch maximal {maxPossibleFans} zusätzliche Lüfter. Bitte reduziere die Anzahl.</span>
                                           </p>
                                      )}
                                  </>
                              )}
                          </div>
                        )}

                        {catKey === 'cpu' && (
                          <div className="flex gap-3 mb-6 2xl:mb-8">
                            <button
                              onClick={() => setSubFilters({ ...subFilters, cpu: 'amd' })}
                              className={`flex-1 flex items-center justify-center gap-2.5 py-3 2xl:py-4 rounded-xl border-2 transition-all ${subFilters.cpu === 'amd' ? 'border-orange-500 bg-orange-500/10 text-orange-600' : 'border-border bg-card text-muted-foreground hover:bg-secondary/50'}`}
                            >
                              <img src={amd_logo} alt="AMD" className="h-5 object-contain" />
                            </button>
                            <button
                              onClick={() => setSubFilters({ ...subFilters, cpu: 'intel' })}
                              className={`flex-1 flex items-center justify-center gap-2.5 py-3 2xl:py-4 rounded-xl border-2 transition-all ${subFilters.cpu === 'intel' ? 'border-blue-500 bg-blue-500/10 text-blue-600' : 'border-border bg-card text-muted-foreground hover:bg-secondary/50'}`}
                            >
                              <img src={intel_logo} alt="Intel" className="h-5 object-contain" />
                            </button>
                          </div>
                        )}

                        {catKey === 'gpu' && (
                          <div className="flex gap-3 mb-6 2xl:mb-8">
                            <button
                              onClick={() => setSubFilters({ ...subFilters, gpu: 'nvidia' })}
                              className={`flex-1 flex items-center justify-center gap-2.5 py-3 2xl:py-4 rounded-xl border-2 transition-all ${subFilters.gpu === 'nvidia' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600' : 'border-border bg-card text-muted-foreground hover:bg-secondary/50'}`}
                            >
                              <img src={nvidia_logo} alt="NVIDIA" className="h-5 object-contain" />
                            </button>
                            <button
                              onClick={() => setSubFilters({ ...subFilters, gpu: 'amd' })}
                              className={`flex-1 flex items-center justify-center gap-2.5 py-3 2xl:py-4 rounded-xl border-2 transition-all ${subFilters.gpu === 'amd' ? 'border-orange-500 bg-orange-500/10 text-orange-600' : 'border-border bg-card text-muted-foreground hover:bg-secondary/50'}`}
                            >
                              <img src={radeon_logo} alt="Radeon" className="h-5 object-contain" />
                            </button>
                          </div>
                        )}

                        {isCoolingCat ? (
                          <div className="flex flex-col">
                            <div className="mb-4 2xl:mb-8">
                              <h3 className="text-sm 2xl:text-base font-bold uppercase tracking-widest text-muted-foreground mb-1">Luftkühler</h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 2xl:gap-3">
                                {coolingAir.map(i => renderComponentItem(i, false))}
                              </div>
                            </div>
                            <div className="mt-2 2xl:mt-4">
                              <h3 className="text-sm 2xl:text-base font-bold uppercase tracking-widest text-muted-foreground mb-1">AiO Wasserkühler</h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 2xl:gap-3">
                                {coolingAio.map(i => renderComponentItem(i, true))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 2xl:gap-3">
                            {displayItems.map(i => renderComponentItem(i))}
                          </div>
                        )}

                        {nextCatKey && nextMeta && (
                          <div className="mt-5 2xl:mt-6">
                            <button
                              onClick={() => { setActiveCategory(nextCatKey); scrollToTop(); }}
                              className="w-full flex items-center justify-between px-4 py-3 2xl:px-5 2xl:py-4 rounded-xl border border-primary/25 bg-primary/5 hover:bg-primary/10 transition-colors group"
                            >
                              <span className="text-xs 2xl:text-sm font-bold text-primary">Weiter: {nextMeta.label}</span>
                              <ArrowRight className="w-4 h-4 2xl:w-5 2xl:h-5 text-primary group-hover:translate-x-1 transition-transform" />
                            </button>
                          </div>
                        )}

                        <FpsDisplay />

                        {compatibility.issues.length > 0 && (
                          <div className="mt-5 2xl:mt-6 bg-destructive/8 border border-destructive/30 rounded-xl p-3 2xl:p-4 flex gap-2 2xl:gap-3 items-start">
                            <ShieldAlert className="w-4 h-4 2xl:w-5 2xl:h-5 text-destructive shrink-0 mt-0.5" />
                            <div>
                              <p className="font-bold text-destructive text-[11px] 2xl:text-sm mb-1 2xl:mb-1.5">Kompatibilitätsprobleme</p>
                              {compatibility.issues.map((iss, i) => <p key={i} className="text-[9px] 2xl:text-sm mb-1 last:mb-0">{iss}</p>)}
                            </div>
                          </div>
                        )}

                        <div className="flex justify-between items-center mt-6 2xl:mt-8">
                          <button onClick={() => { setStep('budget'); scrollToTop(); }} className="px-3 py-1.5 2xl:px-4 2xl:py-2 text-xs 2xl:text-sm font-semibold text-muted-foreground hover:text-foreground">← Zurück</button>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  );
                })()}
              </motion.div>
            )}

            {step === 'review' && (
              <motion.div key="review" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
                <h1 className="text-2xl lg:text-3xl 2xl:text-4xl font-black mb-1 2xl:mb-2"><span className="font-light">Deine </span>Konfiguration.</h1>
                <p className="text-sm 2xl:text-md text-muted-foreground mb-6 2xl:mb-8">Überprüfe alles vor dem Hinzufügen in den Warenkorb.</p>

                {overBudget && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 2xl:p-5 mb-5 2xl:mb-6 flex gap-2 2xl:gap-3 items-start">
                    <AlertTriangle className="w-4 h-4 2xl:w-5 2xl:h-5 text-orange-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-orange-800 text-sm 2xl:text-base mb-1">Budget überschritten</p>
                      <p className="text-xs 2xl:text-base text-orange-700">Komponenten: <strong>€{formatPrice(currentTotal)}</strong> — <strong>€{formatPrice(Math.max(0, currentTotal - budget))} über</strong> Budget.</p>
                    </div>
                  </div>
                )}

                <div className="bg-card border border-border rounded-xl overflow-hidden mb-6 2xl:mb-8">
                  <div className="divide-y divide-border">
                    {CATEGORY_ORDER.map((cat) => {
                      const id = selections[cat];
                      
                      let item = null;
                      if (cat === 'cooling') {
                          item = configuratorData?.air?.find(i => i.id === id) || configuratorData?.water?.find(i => i.id === id);
                      } else {
                          item = configuratorData?.[cat]?.find(i => i.id === id);
                      }

                      const isRec = recommended[cat] === id;

                      // Fester Rahmen für jedes Bild beim Review!
                      return (
                        <div key={cat} className="px-4 py-3 2xl:px-5 2xl:py-4 flex items-center gap-3 2xl:gap-4 hover:bg-muted/30 transition-colors">
                          <div className="w-16 h-10 sm:w-20 sm:h-12 2xl:w-24 2xl:h-16 shrink-0 flex items-center justify-center p-1">
                            {item?.image ? (
                              <img src={item.image} alt={item?.name || ''} className="w-full h-full object-contain" />
                            ) : (
                              <div className="w-full h-full bg-secondary/40 rounded-md border border-border/50 border-dashed flex items-center justify-center">
                                <Package className="w-4 h-4 2xl:w-6 2xl:h-6 text-muted-foreground opacity-30" />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-4">
                            <span className="text-muted-foreground text-[9px] 2xl:text-xs font-semibold uppercase w-20 2xl:w-24 shrink-0">{categoryMeta[cat]?.short}</span>
                            <span className={`font-semibold text-xs 2xl:text-sm ${item ? '' : 'text-destructive'}`}>
                                {item ? (cat === 'fans' && fanQuantity > 1 ? `${fanQuantity}x ` : '') + item.name : 'Nicht ausgewählt'}
                            </span>
                            {isRec && item && <span className="shrink-0 text-[8px] 2xl:text-[10px] bg-accent/20 text-accent-foreground font-semibold px-2 py-0.5 rounded-full w-fit">Empfohlen</span>}
                          </div>
                          <span className="font-bold text-xs 2xl:text-sm ml-2 shrink-0">{item ? (item.price > 0 ? `€${formatPrice(cat === 'fans' ? item.price * fanQuantity : item.price)}` : '€0,00') : '—'}</span>
                        </div>
                      );
                    })}
                    <div className="px-4 py-3 2xl:px-5 2xl:py-4 flex justify-between items-center bg-secondary/30">
                      <div className="flex items-center gap-3 2xl:gap-4">
                        <div className="w-16 sm:w-20 2xl:w-24 shrink-0" />
                        <span className="text-muted-foreground text-[9px] 2xl:text-xs font-semibold uppercase w-20 2xl:w-24 hidden sm:block">Service</span>
                        <span className="font-semibold text-xs 2xl:text-sm">Zusammenbau & Test</span>
                      </div>
                      <span className="font-bold text-xs 2xl:text-sm">€99,00</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <button onClick={() => { setStep('components'); scrollToTop(); }} className="px-3 py-1.5 2xl:px-4 2xl:py-2 text-xs 2xl:text-sm font-semibold text-muted-foreground hover:text-foreground">← Zurück</button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {(step === 'components' || step === 'review') && (
          <ConfigSidebar
            step={step} budget={budget} noBudget={noBudget} selections={selections}
            recommended={recommended} overBudget={overBudget} currentTotal={currentTotal}
            budgetPercent={budgetPercent} compatibility={compatibility}
            onProceed={handleProceedToReview} onAddToCart={handleAddToCart}
            configuratorData={configuratorData} 
          />
        )}

      </div>
    </div>
  );
}